import { faPeopleGroup } from '@fortawesome/pro-solid-svg-icons/faPeopleGroup';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup';
import clsx from 'clsx';
import { createContext, type FC, type PropsWithChildren, useContext, useState } from 'react';
import { Col, ListGroup, ListGroupItem, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import type { PopulationType, VrchatRegion } from '@furality/fox-client/models';
import { ReportError, useIsRegistered, useMutation } from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import FontAwesomeIcon from '#/components/FontAwesomeIcon.tsx';
import useOptimistic from '@/lib/hooks/useOptimistic.ts';
import useProfileQuery from '@/lib/queries/useProfileQuery.ts';
import useToasts from '@/lib/useToast.ts';

type InstancePreference = {
  show: () => void;
  regionPreference: VrchatRegion | null | undefined;
  populationPreference: PopulationType | null | undefined;
};

const InstancePreferenceContext = createContext<InstancePreference>({
  show: () => undefined,
  regionPreference: undefined,
  populationPreference: undefined,
});

export const useInstancePreferences = () => useContext(InstancePreferenceContext);

type InstancePreferenceModalProps = {
  show?: boolean;
  onHide: () => void;
};
const InstancePreferenceModal: FC<InstancePreferenceModalProps> = ({ show, onHide }) => {
  const { registered } = useIsRegistered();

  const { t } = useTranslation();
  const { pushToast } = useToasts();

  const profile = useProfileQuery();

  const setPreferredRegion = useMutation({
    mutationFn: async (preferredRegion: VrchatRegion) => {
      const response = await fox.value.userSetPreferredRegion({
        body: { preferredRegion },
      });
      switch (response.status) {
        case 204:
          pushToast('info', t('shared.navigation.preferred_region_set'));
          return 'ok';
        default:
          pushToast('error', t('shared.navigation.preferred_region_set.error'));
          throw new ReportError(response);
      }
    },
    onSettled: () => void profile.refetch(),
  });
  const setPreferredPopulation = useMutation({
    mutationFn: async (preferredPopulationType: PopulationType) => {
      const response = await fox.value.userSetPreferredPopulationType({
        body: { preferredPopulationType },
      });
      switch (response.status) {
        case 204:
          //pushToast('info', t('shared.navigation.population_type_set'));
          return 'ok';
        default:
          pushToast('error', t('shared.navigation.population_type_set.error'));
          throw new ReportError(response);
      }
    },
    onSettled: () => void profile.refetch(),
  });

  const [selectedRegion, setSelectedRegion] = useOptimistic(profile.data?.user.preferredRegion);
  const [selectedPopulation, setSelectedPopulation] = useOptimistic(
    profile.data?.user.preferredPopulationType,
  );

  const applyPreferences = useMutation({
    mutationFn: () =>
      Promise.all([
        selectedRegion ? setPreferredRegion.mutateAsync(selectedRegion) : null,
        selectedPopulation ? setPreferredPopulation.mutateAsync(selectedPopulation) : null,
      ]).then(onHide),
  });

  if (!registered || profile.isLoading || profile.isPending) {
    return null;
  }

  if (profile.isError) {
    // TODO
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="h5">{t('navigation.set_instance_preferences')}</Modal.Title>
      </Modal.Header>
      <Modal.Body as={Row}>
        <p>{t('navigation.set_preferred_instance_size.header')}</p>
        <Col xs={6}>
          <Modal.Title className="h6 mb-2">{t('navigation.set_preferred_region')}</Modal.Title>
          <ListGroup>
            <ListGroup.Item
              as="button"
              className={clsx('text-left', selectedRegion === 'us' && 'bg-primary')}
              onClick={() => setSelectedRegion('us')}
            >
              <span className="d-flex align-items-center">
                <img
                  height="24"
                  className="mr-2"
                  src="https://media.cdn.furality.online/image/united-states-west-flag-icon.svg"
                  alt="🇺🇸"
                />
                {t('navigation.set_preferred_region.usw')}
              </span>
            </ListGroup.Item>
            <ListGroup.Item
              as="button"
              className={clsx('text-left', selectedRegion === 'use' && 'bg-primary')}
              onClick={() => setSelectedRegion('use')}
            >
              <span className="d-flex align-items-center">
                <img
                  height="24"
                  className="mr-2"
                  src="https://media.cdn.furality.online/image/united-states-east-flag-icon.svg"
                  alt="🇺🇸"
                />
                {t('navigation.set_preferred_region.use')}
              </span>
            </ListGroup.Item>
            <ListGroup.Item
              as="button"
              className={clsx('text-left', selectedRegion === 'eu' && 'bg-primary')}
              onClick={() => setSelectedRegion('eu')}
            >
              <span className="d-flex align-items-center">
                <img
                  src="https://media.cdn.furality.online/image/europe-flag-icon.svg"
                  alt="🇪🇺"
                  height="24"
                  className="mr-2"
                />
                {t('navigation.set_preferred_region.eu')}
              </span>
            </ListGroup.Item>
            <ListGroup.Item
              as="button"
              className={clsx('text-left', selectedRegion === 'jp' && 'bg-primary')}
              onClick={() => setSelectedRegion('jp')}
            >
              <span className="d-flex align-items-center">
                <img
                  src="https://media.cdn.furality.online/image/japan-flag-icon.svg"
                  alt="🇯🇵"
                  height="24"
                  className="mr-2"
                />
                {t('navigation.set_preferred_region.jp')}
              </span>
            </ListGroup.Item>
          </ListGroup>
        </Col>
        <Col xs={6}>
          <Modal.Title className="h6 mb-2">
            {t('navigation.set_preferred_instance_size')}
          </Modal.Title>
          <ListGroup>
            <ListGroupItem
              as="button"
              className={clsx('text-left', selectedPopulation === 'low' && 'bg-primary')}
              onClick={() => setSelectedPopulation('low')}
            >
              <FontAwesomeIcon icon={faUser} className="me-2" />
              {t('navigation.set_preferred_instance_size.low')}
            </ListGroupItem>
            <ListGroupItem
              as="button"
              className={clsx('text-left', selectedPopulation === 'standard' && 'bg-primary')}
              onClick={() => setSelectedPopulation('standard')}
            >
              <FontAwesomeIcon icon={faUserGroup} className="me-2" />
              {t('navigation.set_preferred_instance_size.standard')}
            </ListGroupItem>
            <ListGroupItem
              as="button"
              className={clsx('text-left', selectedPopulation === 'high' && 'bg-primary')}
              onClick={() => setSelectedPopulation('high')}
            >
              <FontAwesomeIcon icon={faPeopleGroup} className="me-2" />
              {t('navigation.set_preferred_instance_size.high')}
            </ListGroupItem>
          </ListGroup>
        </Col>
        <p className="text-muted mt-3">{t('navigation.set_preferred_instance_size.footer')}</p>
        <Button
          onClick={() => applyPreferences.mutate()}
          disabled={!selectedPopulation || !selectedRegion}
        >
          {t('navigation.set_instance_preferences_button')}
        </Button>
      </Modal.Body>
    </Modal>
  );
};

export const InstancePreferenceContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [show, setShow] = useState(false);

  const { registered } = useIsRegistered();
  const profile = useProfileQuery();
  const selectedRegion = profile.data?.user.preferredRegion;
  const selectedPopulation = profile.data?.user.preferredPopulationType;

  return (
    <>
      <InstancePreferenceContext.Provider
        value={{
          // TODO: handle this being called before reg? should be impossible
          show: () =>
            registered
              ? setShow(true)
              : console.error('Attempted setting instance preferences before registered'),
          regionPreference: selectedRegion,
          populationPreference: selectedPopulation,
        }}
      >
        {children}
      </InstancePreferenceContext.Provider>
      <InstancePreferenceModal show={show} onHide={() => setShow(false)} />
    </>
  );
};

export default InstancePreferenceContext;
